<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import Spinner from './Atoms/Spinner.vue';
import ClientOnlyTeleport from '@/Components/ClientOnlyTeleport.vue';
import Icon from '@/Components/Icon.vue';
import { getTenantContext } from '@/composables/useTenant';
import Tooltip from './Tooltip.vue';

const props = defineProps({
    options: {
        type: Array,
        default: () => []
    },
    disabled: {
        type: Boolean,
        default: false
    },
    maxHeight: {
        type: String,
        default: "268px"
    },
    selected: {
        type: Object,
        default: null
    },
    background: {
        type: String,
        default: ''
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    IconColor: {
        type: String,
        default: ''
    },
    // "menu" opens the vertical list below the trigger; "inline" expands the
    // options horizontally next to it. Unknown values keep the menu.
    layout: {
        type: String,
        default: 'menu'
    }
})

const theme = ref(getTenantContext().theme);
const open = ref(false)
const dropdown = ref(null)
const triggerButton = ref(null)
const selected = ref(props.selected)
const dropdownStyle = ref({})
let repositionFrame = 0
const isInline = computed(() => props.layout === 'inline')
const selectedOption = computed({
    get: () => {
        return props.options.find(option => option.id === selected?.value?.id) || props.options[0]
    },
    set: (option) => {
        selected.value = option
    }
})
const emits = defineEmits(['update:modelValue', 'isDownload'])

const selectOption = (option) => {
    selectedOption.value = option;
    const value = option.download_url || option.url ? (option.download_url || option.url) : option.name;
    emits('isDownload', value, option);
    open.value = false;
};

const updateDropdownPosition = () => {
    if (!open.value || !triggerButton.value || typeof window === 'undefined') {
        return;
    }

    const rect = triggerButton.value.getBoundingClientRect();
    dropdownStyle.value = {
        top: `${rect.bottom + 4}px`,
        right: `${Math.max(window.innerWidth - rect.right, 0)}px`,
        maxHeight: props.maxHeight,
    };
};

// A late image can push the trigger down without scrolling or resizing the
// window. Re-anchor the teleported, fixed-position menu after image layout.
const repositionAfterImageLoad = (event) => {
    if (!open.value || !(event.target instanceof HTMLImageElement) || repositionFrame) {
        return;
    }

    repositionFrame = window.requestAnimationFrame(() => {
        repositionFrame = 0;
        updateDropdownPosition();
    });
};

const toggleDropdown = async () => {
    if (props.disabled) {
        return;
    }

    open.value = !open.value;

    if (open.value && !isInline.value) {
        await nextTick();
        updateDropdownPosition();
    }
};

const closeOnOutsidePointer = (event) => {
    if (open.value && isInline.value && !dropdown.value?.contains(event.target)) {
        open.value = false;
    }
};

const closeOnEscape = (e) => {
    if (open.value && e.key === 'Escape') {
        open.value = false;
    }
};

watch(() => props.disabled, (disabled) => {
    if (disabled && open.value) {
        open.value = false;
    }
});

watch(() => props.maxHeight, () => {
    updateDropdownPosition();
});

onMounted(async () => {
    document.addEventListener('keydown', closeOnEscape)
    document.addEventListener('pointerdown', closeOnOutsidePointer, true)
    document.addEventListener('load', repositionAfterImageLoad, true)
    window.addEventListener('resize', updateDropdownPosition)
    document.addEventListener('scroll', updateDropdownPosition, true)
});

onUnmounted(() => {
    document.removeEventListener('keydown', closeOnEscape)
    document.removeEventListener('pointerdown', closeOnOutsidePointer, true)
    document.removeEventListener('load', repositionAfterImageLoad, true)
    window.removeEventListener('resize', updateDropdownPosition)
    document.removeEventListener('scroll', updateDropdownPosition, true)
    if (repositionFrame) window.cancelAnimationFrame(repositionFrame)
});
</script>

<template>
    <div class="dropdown relative focus:outline" :class="[
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        isInline ? ['download-button-inline flex items-center overflow-hidden transition-[max-width] duration-500 ease-in-out motion-reduce:transition-none', open ? 'download-button-inline-open max-w-[320px]' : 'max-w-[42px]'] : '',
    ]" ref="dropdown">
        <button title="Scarica" :aria-expanded="open"
            class="download-button-trigger relative flex shrink-0 border-none bg-principal items-center rounded-b-input gap-2 justify-center border border-principal rounded-t-input text-xs font-medium tracking-wider p-0 w-[42px] h-[42px] height_btn width_btn z-[4]"
            :class="[
                { '!bg-secundario !border-secundario': theme === 'musmeci', 'cursor-not-allowed': disabled },
                `bg-${background}`,
            ]" tabindex="0" ref="triggerButton" type="button" @click="toggleDropdown" :disabled="disabled">
            <Icon name="download" :color="IconColor" aria-hidden="true" v-show="!isLoading" />
            <Spinner :show-spinner="isLoading" v-if="isLoading" class="!h-4 !w-4 invert"></Spinner>
        </button>
        <ul v-if="isInline" class="download-button-inline-options flex shrink-0 items-center px-3" :inert="!open">
            <li v-for="option in options" :key="option.id ?? option.name">
                <button type="button" :title="`Scarica ${option.name}`"
                    class="download-button-inline-option whitespace-nowrap px-2.5 py-1 text-xs font-semibold tracking-wider text-principal"
                    @click="selectOption(option)">
                    {{ option.name }}
                </button>
            </li>
        </ul>
    </div>

    <ClientOnlyTeleport v-if="!isInline">
        <div v-if="open && !disabled && options && selectedOption">
            <div class="fixed inset-0 z-[1099]" @click="open = false"></div>
            <Transition appear enter-active-class="transition duration-300 transform"
                enter-from-class="opacity-0 -translate-y-1" enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition duration-300 transform" leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-4">
                <ul class="download-button-menu fixed flex flex-col w-auto border-x border-b border-t rounded-t-input border-principal rounded-b-input z-[1100] overflow-y-scroll overflow-x-hidden"
                    :class="[`bg-[var(--bg-download)]`]" :style="dropdownStyle" @click.stop>
                    <li type="button"
                        class="group flex cursor-pointer items-center gap-2 overflow-hidden border-b last:border-b-0 p-3 transition-colors duration-150 hover:bg-neutral-100 focus:bg-neutral-100"
                        tabindex="0" v-for="option in options" :key="option"
                        @click="selectOption(option)">
                        <span class="text-principal text-xs font-medium tracking-wider">
                            {{ option.name }}
                        </span>
                    </li>
                </ul>
            </Transition>
        </div>
    </ClientOnlyTeleport>
</template>

<style>
.dropdown ul::-webkit-scrollbar,
.download-button-menu::-webkit-scrollbar {
    display: none;
}

.dropdown ul,
.download-button-menu {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.download-button-menu .customDropDownLabelChildrenText {
    display: block;
    min-width: 0;
    flex: 1 1 0%;
    overflow: hidden;
    color: var(--color-principal);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-align: left;
    text-overflow: ellipsis;
    text-transform: uppercase;
    transition: color 150ms;
    white-space: nowrap;
}
</style>
